import axiosInstance from "../api/axiosInstance";
import {
  getClassBookingActionErrorMessage,
  getClassBookingQueryErrorMessage,
  getErrorStatus,
  getTechnicalErrorMessage,
} from "../utils/uiErrorMessages";
import { repartitionBookings } from "../utils/bookingStatus";
import { logBookingApiResult } from "../utils/datadogLogger";

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const normalizeBookingActionPayload = (payload) => {
  if (!isPlainObject(payload)) {
    return payload || {};
  }

  const nestedData = isPlainObject(payload.data) ? payload.data : null;
  return nestedData ? { ...payload, ...nestedData } : payload;
};

export const normalizeBookingsPayload = (payload) => {
  const data = isPlainObject(payload?.data) ? payload.data : payload;
  const repartitioned = repartitionBookings({
    upcoming: Array.isArray(data?.upcoming) ? data.upcoming : [],
    past: Array.isArray(data?.past) ? data.past : [],
  });
  const summary = isPlainObject(data?.summary) ? data.summary : {};

  return {
    summary: {
      pending: repartitioned.summary.pending,
      upcoming: repartitioned.summary.upcoming,
      past: repartitioned.summary.past,
      total: repartitioned.summary.total,
    },
    pending: repartitioned.pending,
    upcoming: repartitioned.upcoming,
    past: repartitioned.past,
  };
};

export const bookingService = {
  getBookings: async () => {
    const url = "api/bookings/";
    try {
      const response = await axiosInstance.get(url);
      const normalizedPayload = normalizeBookingsPayload(response.data);
      logBookingApiResult({
        api_name: "bookings_list",
        url,
        status_code: response?.status || 200,
        response_body: normalizedPayload,
      });
      return normalizedPayload;
    } catch (error) {
      logBookingApiResult({
        api_name: "bookings_list",
        url,
        status_code: error?.response?.status || 500,
        response_body: error?.response?.data || null,
        error_message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load bookings.",
      });
      const normalizedError = new Error(
        getClassBookingQueryErrorMessage(error, "bookings")
      );
      normalizedError.status = getErrorStatus(error);
      normalizedError.raw = error?.response?.data || null;
      normalizedError.technicalMessage = getTechnicalErrorMessage(error);
      throw normalizedError;
    }
  },
  holdBooking: async (payload) => {
    const url = "api/bookings/hold/";
    try {
      const response = await axiosInstance.post(url, payload);
      const normalizedPayload = normalizeBookingActionPayload(response.data);
      logBookingApiResult({
        api_name: "bookings_hold",
        url,
        status_code: response?.status || 200,
        request_body: payload,
        response_body: normalizedPayload,
        booking_id:
          normalizedPayload?.booking_id || normalizedPayload?.booking?.booking_id,
        booking_source: payload?.booking_source || null,
      });
      return normalizedPayload;
    } catch (error) {
      logBookingApiResult({
        api_name: "bookings_hold",
        url,
        status_code: error?.response?.status || 500,
        request_body: payload,
        response_body: error?.response?.data || null,
        error_message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to hold booking.",
        booking_source: payload?.booking_source || null,
      });
      const normalizedError = new Error(
        getClassBookingActionErrorMessage(error, "hold-slot")
      );
      normalizedError.status = getErrorStatus(error);
      normalizedError.raw = error?.response?.data || null;
      normalizedError.technicalMessage = getTechnicalErrorMessage(error);
      throw normalizedError;
    }
  },
  createCheckoutSession: async (payload) => {
    const url = "api/bookings/checkout-session/";
    try {
      const response = await axiosInstance.post(url, payload);
      const normalizedPayload = normalizeBookingActionPayload(response.data);
      logBookingApiResult({
        api_name: "bookings_checkout_session",
        url,
        status_code: response?.status || 200,
        request_body: payload,
        response_body: normalizedPayload,
        booking_id:
          normalizedPayload?.booking_id || normalizedPayload?.booking?.booking_id,
        checkout_session_id:
          normalizedPayload?.checkout_session_id ||
          normalizedPayload?.session_id ||
          normalizedPayload?.booking?.checkout_session_id,
        booking_source: payload?.booking_source || null,
      });
      return normalizedPayload;
    } catch (error) {
      logBookingApiResult({
        api_name: "bookings_checkout_session",
        url,
        status_code: error?.response?.status || 500,
        request_body: payload,
        response_body: error?.response?.data || null,
        error_message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create booking checkout session.",
        booking_id: payload?.booking_id || null,
        booking_source: payload?.booking_source || null,
      });
      const normalizedError = new Error(
        getClassBookingActionErrorMessage(error, "checkout-session")
      );
      normalizedError.status = getErrorStatus(error);
      normalizedError.raw = error?.response?.data || null;
      normalizedError.technicalMessage = getTechnicalErrorMessage(error);
      throw normalizedError;
    }
  },
  confirmBooking: async (payload) => {
    const url = "api/bookings/confirm/";
    try {
      const response = await axiosInstance.post(url, payload);
      const normalizedPayload = normalizeBookingActionPayload(response.data);
      logBookingApiResult({
        api_name: "bookings_confirm",
        url,
        status_code: response?.status || 200,
        request_body: payload,
        response_body: normalizedPayload,
        booking_id:
          normalizedPayload?.booking_id || normalizedPayload?.booking?.booking_id,
        checkout_session_id:
          payload?.checkout_session_id ||
          normalizedPayload?.checkout_session_id ||
          normalizedPayload?.booking?.checkout_session_id,
        booking_source: payload?.booking_source || null,
      });
      return normalizedPayload;
    } catch (error) {
      logBookingApiResult({
        api_name: "bookings_confirm",
        url,
        status_code: error?.response?.status || 500,
        request_body: payload,
        response_body: error?.response?.data || null,
        error_message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to confirm booking payment.",
        booking_id: payload?.booking_id || null,
        checkout_session_id: payload?.checkout_session_id || null,
        booking_source: payload?.booking_source || null,
      });
      const normalizedError = new Error(
        getClassBookingActionErrorMessage(error, "confirm-booking")
      );
      normalizedError.status = getErrorStatus(error);
      normalizedError.raw = error?.response?.data || null;
      normalizedError.technicalMessage = getTechnicalErrorMessage(error);
      throw normalizedError;
    }
  },
};
